import React, { useMemo, useState } from "react";
import styled from "styled-components/native";
import LabelView from "../LabelView";
import { useTheme } from "styled-components/native";
import { Pressable, View } from "react-native";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { FlatList } from "react-native-gesture-handler";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { PriceModes } from "../PriceModeCard/types";
import { ProductCategory } from "@utils/constants";
import { useItemDetailsCardSubSections } from "../../../hooks/useSortCustomData";
import { useUserCurrency } from "../../../hooks/getCurrency";
import { useTranslation } from "react-i18next";
import { formatItemValue, SecondSectionWithFilteredDataTypes } from "./types";
import { useSelector } from "react-redux";
import { SpaceType } from "@components/atoms/Spacer/types";
import { css } from "styled-components/native";
import { SearchFilterTypeEnum } from "@components/atoms/Filters/utils";
import { isLinkedPair } from "@utils/product/productValidators";
import { StoneType } from "@scenes/bulk-request/Utils";
import Spacer from "@components/atoms/Spacer";
import { VDBText } from "@components/atoms";
import { useIsFoldable } from "@navigations/utils/FoldableWrapper";

const SecondSectionWithFilteredPairData = ({
  listExpanded,
  mItem,
  productType,
  fromBulkList,
  priceDetailsDataOptions,
  editBulkItem,
  sectionName = SearchFilterTypeEnum.SINGLE_STONES,
  handleLinkClick,
}: SecondSectionWithFilteredDataTypes) => {
  const theme = useTheme();
  const userInfo = useUserInfo();
  const isCustomApp = useSelector((state) => state?.appState?.isCustomApp);
  const priceMode = userInfo?.user?.price_mode;
  const { t } = useTranslation();
  const userCurrency = useUserCurrency();
  const typography = useTypographyStore();
  const { deviceWidth } = useIsFoldable();
  const screenWidth = deviceWidth;
  const [containerWidth, setContainerWidth] = useState(0);
  const { itemDetailsDataOptions, defaultRows, defaultRowsWeb } =
    useItemDetailsCardSubSections(
      productType as ProductCategory,
      priceMode,
      sectionName,
      fromBulkList,
      StoneType.PAIR
    ) || {};

  const filteredData = useMemo(() => {
    return itemDetailsDataOptions?.filter((item: any) => {
      if (item.name === "discountPercent" && mItem?.is_fancy === true) {
        return false;
      }
      return true;
    });
  }, [itemDetailsDataOptions, mItem]);

  const filteredDisplayData = useMemo(() => {
    return !listExpanded
      ? filteredData?.slice(
          0,
          (theme.isMobile ? defaultRows : defaultRowsWeb) * 2
        )
      : filteredData;
  }, [listExpanded, filteredData]);

  return (
    <ParentContainer>
      {sectionName === SearchFilterTypeEnum.PAIRS && isLinkedPair(mItem)
        ? mItem?.children?.map((child: any, index: number) => (
            <SecondContainer
              theme={theme}
              contentContainerStyle={{
                marginLeft: SpaceType.Basic,
                marginRight: SpaceType.Basic,
              }}
              priceMode={priceMode}
              priceDetailsDataOptions={priceDetailsDataOptions}
              onLayout={(e) => {
                const removeExtraPadding = editBulkItem ? 40 : 0;
                setContainerWidth(
                  e.nativeEvent.layout.width - removeExtraPadding
                );
              }}
              addRightBorder={index === 0}
            >
              {(theme.isMobile || !!containerWidth) && (
                <FlatList
                  data={filteredDisplayData}
                  contentContainerStyle={{ margin: 5 }}
                  renderItem={({ item }: any) =>
                    item.price_modes.includes(priceMode) &&
                    (item.show_popover_data &&
                    child?.[item?.popover_data_response_key] ? (
                      <Pressable
                        style={{
                          marginBottom: SpaceType.ExtraSmall,
                          marginTop: SpaceType.ExtraSmall,
                        }}
                        onPress={(e) => {
                          e.preventDefault();
                          handleLinkClick(
                            child,
                            item?.popover_data_response_key
                          );
                        }}
                      >
                        <LabelView
                          itemLeftText={t(`${item.label}`)}
                          itemRightText={formatItemValue(
                            isCustomApp,
                            item.response_key,
                            child,
                            userCurrency,
                            userInfo,
                            t
                          )}
                          keyWidth={40}
                          cellWidth={130}
                          rightPrefixTextTheme={theme["link-static"]}
                          keyTypographyStyle={typography["Minor-R"]}
                          valueTypographyStyle={typography["Minor-M"]}
                        />
                      </Pressable>
                    ) : (
                      <View
                        style={{
                          marginBottom: SpaceType.ExtraSmall,
                          marginTop: SpaceType.ExtraSmall,
                        }}
                      >
                        <TitleContainer screenWidth={screenWidth}>
                          <Spacer size={SpaceType.ExtraSmall} />
                          <TextContainer>
                            <KeyContainer>
                              <VDBText
                                type={typography["Minor-R"]}
                                color={theme["core-06"]}
                                ellipsizeMode="tail"
                              >
                                {t(`${item.label}`)}
                              </VDBText>
                            </KeyContainer>
                            <Spacer size={SpaceType.ExtraSmall} />
                            <VDBText
                              type={typography["Minor-M"]}
                              color={theme["core-05"]}
                              numberOfLines={1}
                              ellipsizeMode="tail"
                            >
                              {formatItemValue(
                                isCustomApp,
                                item.response_key,
                                child,
                                userCurrency,
                                userInfo,
                                t
                              )}
                            </VDBText>
                          </TextContainer>
                        </TitleContainer>
                      </View>
                    ))
                  }
                  keyExtractor={(item: any, _index: number) =>
                    `${item?.id}-${_index}`
                  }
                />
              )}
            </SecondContainer>
          ))
        : null}
    </ParentContainer>
  );
};

const SecondContainer = styled.View<any>`
  width: 50%;
  background-color: ${({ theme }) => theme["core-01"]};
  justify-content: flex-start;
  border-bottom-left-radius: ${({ priceMode, priceDetailsDataOptions }) =>
    priceMode === PriceModes.GREEN || priceDetailsDataOptions?.length === 0
      ? 8
      : 0}px;
  border-bottom-right-radius: ${({ priceMode, priceDetailsDataOptions }) =>
    priceMode === PriceModes.GREEN || priceDetailsDataOptions?.length === 0
      ? 8
      : 0}px;
  ${({ theme, addRightBorder }) =>
    addRightBorder &&
    css`
      border-right-width: 1px;
      border-right-color: ${theme["core-04"]};
    `}
`;

const ParentContainer = styled.View<any>`
  flex-direction: row;
  width: 100%;
  border-top-width: 1px;
  border-bottom-width: 1px;
  border-color: ${({ theme }) => theme["core-04"]};
  background-color: ${({ theme }) => theme["core-01"]};
`;

const TitleContainer = styled.View<any>`
  width: ${({ screenWidth }) => screenWidth / 2.6}px;
`;

const TextContainer = styled.View`
  flex-direction: row;
`;

const KeyContainer = styled.View`
  width: 40px;
`;

export default SecondSectionWithFilteredPairData;
